// src/pages/mikrotik/TerminalPage.tsx
import React, { useState } from "react";
import { ArrowRightCircle, TerminalSquare, RefreshCw } from "lucide-react";

const API_BASE_URL =

  "http://localhost:8080/api/v1/terminal";

const token =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJob3N0IjoiMTAuMTMuMTMuMiIsInVzZXJuYW1lIjoiYWRtaW4iLCJwYXNzd29yZCI6IjIyMTEifQ.SbsT5SADV3xSGavXCMDifDLh7BQkjlyTosVOtJGjcxg";

interface TerminalResponse {
  command: string;
  output: string;
}

interface HistoryEntry extends TerminalResponse {
  id: number;
  timestamp: string;
  status: "ok" | "error";
  errorMessage?: string;
}

const authHeaders = (): HeadersInit => {
  const base: HeadersInit = {
    "Content-Type": "application/json",
  };
  if (!token) return base;
  return { ...base, Authorization: `Bearer ${token}` };
};

const TerminalPage: React.FC = () => {
  const [command, setCommand] = useState("");
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [running, setRunning] = useState(false);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  const resetBanner = () => setErrorBanner(null);

  const handleRun = async () => {
    const trimmed = command.trim();
    if (!trimmed) return;

    resetBanner();
    setRunning(true);

    const timestamp = new Date().toLocaleTimeString();
    const baseEntry: HistoryEntry = {
      id: Date.now(),
      command: trimmed,
      output: "",
      timestamp,
      status: "ok",
    };

    try {
      const res = await fetch(`${API_BASE_URL}/run`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ command: trimmed }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const detail =
          (errData && (errData.detail || errData.error)) ||
          "Command failed";

        const entry: HistoryEntry = {
          ...baseEntry,
          status: "error",
          errorMessage: detail,
          output: "",
        };

        setHistory((prev) => [...prev, entry]);
        setErrorBanner(detail);
        return;
      }

      const data: TerminalResponse = await res.json();

      const entry: HistoryEntry = {
        ...baseEntry,
        status: "ok",
        output: data.output || "",
      };

      setHistory((prev) => [...prev, entry]);
    } catch (err: any) {
      const msg = err?.message || "Failed to run command";
      const entry: HistoryEntry = {
        ...baseEntry,
        status: "error",
        errorMessage: msg,
        output: "",
      };
      setHistory((prev) => [...prev, entry]);
      setErrorBanner(msg);
    } finally {
      setRunning(false);
      setCommand("");
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (
    e
  ) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (!running) handleRun();
    }
  };

  const clearHistory = () => {
    setHistory([]);
    resetBanner();
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 lg:px-8">
      <div className="mx-auto max-w-6xl space-y-4">
        {/* Header */}
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-900 text-white">
              <TerminalSquare className="h-5 w-5" />
            </span>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
                Terminal
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                Run RouterOS commands directly from the web interface. Use{" "}
                <span className="rounded bg-slate-100 px-1 py-0.5 text-xs font-mono">
                  Ctrl + Enter
                </span>{" "}
                to execute.
              </p>
            </div>
          </div>

          <button
            onClick={clearHistory}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-100"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Clear History
          </button>
        </header>

        {/* Error banner */}
        {errorBanner && (
          <div className="flex items-center justify-between gap-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 shadow-sm">
            <p>{errorBanner}</p>
            <button
              onClick={resetBanner}
              className="rounded-full px-2 py-1 text-xs hover:bg-red-100"
            >
              Close
            </button>
          </div>
        )}

        {/* Terminal area fills screen height; input stays at bottom */}
        <section className="mt-2 flex h-[calc(100vh-160px)] flex-col rounded-2xl border border-slate-200 bg-white shadow-sm">
          {/* Title bar */}
          <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
              <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
              <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
              <span className="ml-3 text-xs font-medium text-slate-600">
                MikroTik
              </span>
            </div>
            <span className="text-[11px] text-slate-400">
              x86_64 • Web terminal
            </span>
          </div>

          {/* History (scrollable, takes remaining height) */}
          <div className="flex-1 overflow-y-auto bg-white px-4 py-3 font-mono text-[13px] leading-relaxed text-slate-900">
            {history.length === 0 && (
              <div className="py-10 text-center text-xs text-slate-500">
                No commands yet. Try{" "}
                <span className="font-mono text-emerald-600">
                  /ip address print
                </span>{" "}
                or{" "}
                <span className="font-mono text-emerald-600">
                  /system resource print
                </span>
                .
              </div>
            )}

            {history.map((entry) => (
              <div key={entry.id} className="mb-4">
                <div className="flex flex-wrap gap-1">
                  <span className="text-emerald-700">
                    [admin@MikroTik] &gt;
                  </span>
                  <span className="text-emerald-700">{entry.command}</span>
                </div>

                {entry.status === "error" && entry.errorMessage ? (
                  <pre className="mt-1 whitespace-pre-wrap text-[13px] text-red-600">
{entry.errorMessage}
                  </pre>
                ) : (
                  entry.output && (
                    <pre className="mt-1 whitespace-pre-wrap text-[13px] text-slate-900">
{entry.output}
                    </pre>
                  )
                )}
              </div>
            ))}
          </div>

          {/* Input bar – always at bottom of the card / screen */}
          <div className="border-t border-slate-200 bg-slate-50 px-4 py-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="flex flex-1 items-start gap-2">
                <span className="mt-2 text-[13px] font-mono text-emerald-700">
                  [admin@MikroTik] &gt;
                </span>
                <textarea
                  rows={1}
                  className="w-full resize-none rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-[13px] font-mono text-slate-900 outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
                  placeholder="/ip address print"
                  value={command}
                  onChange={(e) => setCommand(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>

              <div className="flex w-full gap-2 sm:w-auto">
                <button
                  type="button"
                  onClick={handleRun}
                  disabled={running || !command.trim()}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-emerald-500 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <ArrowRightCircle className="h-4 w-4" />
                  {running ? "Running…" : "Run"}
                </button>
                <button
                  type="button"
                  onClick={() => setCommand("")}
                  className="inline-flex w-full items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition hover:bg-slate-100"
                >
                  Clear line
                </button>
              </div>
            </div>
            <p className="mt-1 text-[11px] text-slate-500">
              Tip: press{" "}
              <span className="rounded bg-white px-1 py-0.5 font-mono">
                Ctrl + Enter
              </span>{" "}
              to execute the current command.
            </p>
          </div>

          {/* Status bar */}
          <div className="border-t border-slate-200 bg-white px-4 py-1.5 text-[11px] text-slate-500 flex flex-wrap gap-4">
            <span>MikroTik Web Console</span>
            <span className="hidden sm:inline">CPU: 0%</span>
            <span className="hidden sm:inline">Uptime: —</span>
            <span className="ml-auto">
              {new Date().toLocaleDateString()}{" "}
              {new Date().toLocaleTimeString()}
            </span>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TerminalPage;
